import React from 'react';
import { Typography, Button, Dialog, DialogTitle, Box, DialogContent, DialogActions } from '@mui/material';

/**
 * @param {object} props
 * @param {ConfirmationModalProps['title']} props.title
 * @param {ConfirmationModalProps['titleIcon']} props.titleIcon
 * @param {ConfirmationModalProps['confirmColour']} props.confirmColour
 * @returns
 */
const Title = ({ title, titleIcon, confirmColour }) => {
  if (typeof title === 'string') {
    return (
      <Typography variant="h6" component="div" sx={{ display: 'inline-block', alignItems: 'center', gap: 1 }}>
        <TitleIcon icon={titleIcon} color={confirmColour} />
        {title}
      </Typography>
    );
  }

  return (
    <>
      <TitleIcon icon={titleIcon} color={confirmColour} />
      {title}
    </>
  );
};

/**
 * @param {object} props
 * @param {ConfirmationModalProps['titleIcon']} props.icon
 * @param {ConfirmationModalProps['confirmColour']} props.color
 * @returns
 */
const TitleIcon = ({ icon, color }) => {
  if (!icon) {
    return null;
  }

  // If it's a ReactElement
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, {
      color: icon.props.color ?? color,
      style: {
        ...icon.props.style,
        marginRight: '0.5rem',
        verticalAlign: 'sub',
      },
    });
  }

  // If it's a component type (function/class)
  const IconComponent = icon;

  return <IconComponent color={color} style={{ marginRight: '0.5rem', verticalAlign: 'sub' }} />;
};

/**
 * @param {object} props
 * @param {ConfirmationModalProps['description']} props.description
 * @returns
 */
const Description = ({ description }) => {
  if (typeof description === 'string') {
    return <Typography>{description}</Typography>;
  }

  return description;
};

/**
 * @typedef {import('@mui/material').DialogProps} DialogPropsMui
 */

/**
 * @typedef {Parameters<NonNullable<DialogPropsMui['onClose']>>[1] | 'cancelClick'} DialogCloseReason
 */

/**
 * @callback DialogOnClose
 * @param {{} | React.SyntheticEvent} event
 * @param {DialogCloseReason} reason
 * @returns {void}
 */

/**
 * @typedef {Omit<import('@mui/material').DialogProps, 'onClose' | 'title'> & {
 *   title: React.ReactElement | string;
 *   titleIcon?: React.ReactElement | React.ComponentType<import('@mui/material').SvgIconProps> | null;
 *   description?: React.ReactNode;
 *   confirmText?: string;
 *   cancelText?: string;
 *   confirmColour?: import('@mui/material').ButtonProps['color'];
 *   isLoading?: boolean;
 *   onConfirm?: () => Promise<void> | void;
 *   onClose?: DialogOnClose;
 *   nonDismissable?: boolean;
 * }} ConfirmationModalProps
 */

/**
 * @param {ConfirmationModalProps} props
 */
export const ConfirmationModal = ({
  title,
  titleIcon,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColour = 'primary',
  isLoading = false,
  nonDismissable = false,
  onClose,
  onConfirm,
  ...props
}) => {
  /**
   * @param {React.MouseEvent<HTMLButtonElement>} e
   */
  const handleOnCancel = (e) => {
    onClose?.(e, 'cancelClick');
  };

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      onClose={nonDismissable ? undefined : onClose}
      {...props}
      slotProps={{
        ...props.slotProps,
        paper: {
          elevation: 3,
          ...props.slotProps?.paper,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Title title={title} titleIcon={titleIcon} confirmColour={confirmColour} />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Description description={description} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!!onClose && (
          <Button disableElevation onClick={handleOnCancel} disabled={isLoading}>
            {cancelText}
          </Button>
        )}
        <Button disableElevation onClick={onConfirm} variant="contained" color={confirmColour} loading={isLoading}>
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
